// client/src/components/admin/AdminLayout.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  Badge,
  Avatar,
  MenuItem,
  Paper
} from '@mui/material';
import {
  Menu,
  Dashboard,
  People,
  Task,
  Payment,
  Help,
  Analytics,
  Settings,
  Logout,
  AdminPanelSettings,
  Chat,
  AccountCircle,
  Notifications
} from '@mui/icons-material';

const drawerWidth = 240;

export default function AdminLayout({ children, pageTitle = "Dashboard" }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [notificationPolling, setNotificationPolling] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [previousUnreadCount, setPreviousUnreadCount] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
    handleMenuClose();
  };

  // Function to check for unread admin messages
  const checkAdminUnreadMessages = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) return 0;

      // Fetch all help requests (conversations)
      const response = await fetch('https://propertyearn.net/api/api/admin/help-requests', {
        headers: { 
          'Authorization': `Bearer ${token}` 
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        let totalUnread = 0;
        
        if (data.requests && data.requests.length > 0) {
          // Count active chat conversations as unread notifications
          data.requests.forEach(request => {
            if (request.is_chat_conversation && !request.is_resolved) {
              totalUnread += 1; // Each active conversation counts as 1 notification
            }
          });
        }
        
        console.log('🔔 Admin unread messages count:', totalUnread);
        
        // Show notification if new messages arrived
        if (totalUnread > previousUnreadCount) {
          playNotificationSound();
          setShowNotification(true);
          
          // Auto-hide notification after 3 seconds
          setTimeout(() => setShowNotification(false), 3000);
        }
        
        setPreviousUnreadCount(totalUnread);
        setUnreadMessages(totalUnread);
        
        return totalUnread;
      }
    } catch (error) {
      console.error('❌ Error checking admin unread messages:', error);
    }
    return 0;
  };

  // Sound notification function
  const playNotificationSound = () => {
    try {
      // Simple beep sound using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
      
    } catch (error) {
      console.log('🔕 Sound notification not supported:', error);
    }
  };

  // Real-time notification polling
  useEffect(() => {
    // Check immediately when component loads
    checkAdminUnreadMessages();
    
    // Set up polling every 15 seconds for new messages
    const interval = setInterval(() => {
      checkAdminUnreadMessages();
    }, 15000);
    
    // Store interval reference
    setNotificationPolling(interval);
    
    // Cleanup interval on component unmount
    return () => {
      if (interval) {
        clearInterval(interval);
        console.log('🛑 Stopped admin notification polling');
      }
    };
  }, []);

  const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/admin/dashboard' },
    { text: 'User Management', icon: <People />, path: '/admin/users' },
    { text: 'Task Moderation', icon: <Task />, path: '/admin/tasks' },
    { text: 'Create Admin', icon: <AdminPanelSettings />, path: '/admin/register' },
    { text: 'Financial Oversight', icon: <Payment />, path: '/admin/financial' },
    { text: 'Help Requests', icon: <Help />, path: '/admin/HelpRequests' },
    { text: 'Reports & Analytics', icon: <Analytics />, path: '/admin/analytics' },
    { text: 'System Configuration', icon: <Settings />, path: '/admin/config' },
  ];

  const drawer = (
    <Box>
      <Toolbar sx={{ backgroundColor: 'primary.main', color: 'white' }}>
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold' }}>
          Admin Panel
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton 
              onClick={() => navigate(item.path)}
              selected={location.pathname === item.path}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                }
              }}
            >
              <ListItemIcon sx={{ 
                color: location.pathname === item.path ? 'white' : 'inherit'
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text}
                sx={{
                  '& .MuiListItemText-primary': {
                    fontSize: '0.9rem',
                    fontWeight: location.pathname === item.path ? 'bold' : 'normal'
                  }
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon>
              <Logout />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* Add CSS animations */}
      <style>
        {`
          @keyframes pulse {
            0% {
              transform: scale(1);
              box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.7);
            }
            50% {
              transform: scale(1.1);
              box-shadow: 0 0 0 10px rgba(244, 67, 54, 0);
            }
            100% {
              transform: scale(1);
              box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
            }
          }

          @keyframes flash {
            0%, 50%, 100% {
              opacity: 1;
            }
            25%, 75% {
              opacity: 0.5;
            }
          }
        `}
      </style>

      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <Menu />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {pageTitle}
          </Typography>

          {/* Notification and Profile Icons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Notifications Icon */}
            <IconButton color="inherit" title="Notifications">
              <Badge badgeContent={0} color="error">
                <Notifications />
              </Badge>
            </IconButton>
            
            {/* Chat Icon with Notification Badge */}
            <IconButton 
              color="inherit"
              onClick={() => navigate('/admin/HelpRequests')}
              sx={{
                position: 'relative',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
              title={`${unreadMessages} unread messages`}
            >
              <Badge 
                badgeContent={unreadMessages} 
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.7rem',
                    height: '18px',
                    minWidth: '18px',
                    backgroundColor: '#f44336',
                    animation: unreadMessages > 0 ? 'pulse 2s infinite' : 'none',
                  }
                }}
              >
                <Chat />
              </Badge>
              
              {/* Red dot indicator */}
              {unreadMessages > 0 && (
                <Box
                  component="span"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: '#ff4444',
                    animation: 'pulse 1.5s infinite'
                  }}
                />
              )}
            </IconButton>

            {/* Profile Menu */}
            <IconButton color="inherit" onClick={handleProfileMenuOpen}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                <AccountCircle />
              </Avatar>
            </IconButton>
          </Box>

          {/* Profile Dropdown Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: {
                mt: 1.5,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1,
                  fontSize: '0.9rem'
                }
              }
            }}
          >
            <MenuItem onClick={() => navigate('/admin/profile')}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={() => navigate('/admin/settings')}>
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              border: 'none',
              boxShadow: '2px 0 8px rgba(0,0,0,0.1)'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          p: 3, 
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: 'grey.50',
          minHeight: '100vh'
        }}
      >
        <Toolbar /> {/* Spacer for AppBar */}

        {/* Floating Notification Alert */}
        {showNotification && (
          <Paper
            elevation={3}
            sx={{
              position: 'fixed',
              top: 80,
              right: 20,
              zIndex: 9999,
              p: 2,
              backgroundColor: 'primary.main',
              color: 'white',
              animation: 'flash 2s ease-in-out',
              minWidth: 200,
              border: '2px solid #ff4444',
              borderRadius: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chat sx={{ fontSize: 20 }} />
              <Typography variant="body2" fontWeight="bold">
                New message received!
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>
              You have {unreadMessages} unread conversation{unreadMessages !== 1 ? 's' : ''}
            </Typography>
          </Paper>
        )}

        {/* Page Content */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            minHeight: 'calc(100vh - 100px)',
            borderRadius: 2,
            backgroundColor: 'white'
          }}
        >
          {children}
        </Paper>
      </Box>
    </Box>
  );
}